import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, problemMessage, unwrap } from "./client";
import { jdKeys } from "./jd-queries";
import type { components } from "./schema";

/**
 * A job description's own questions, its matches, its pick preview and the pick policies — DESIGN
 * §20.6, §20.7, §20.9. Beside jd-queries.ts (Phase 1's), same conventions: generated types, `unwrap`
 * for the API's own error sentence, prefix invalidation under `jdKeys.all`.
 */

type Schemas = components["schemas"];
export type JDQuestions = Schemas["JDQuestionsOut"];
export type JDQuestion = Schemas["JDQuestionOut"];
export type JDAddQuestionsOut = Schemas["JDAddQuestionsOut"];
export type JDRematchOut = Schemas["JDRematchOut"];
export type JDPolicy = Schemas["JDPolicyOut"];
export type PickOut = Schemas["PickOut"];
export type PickSource = NonNullable<Schemas["PickItem"]["source"]>;
export type Scope = "bank" | "jd_only";

export const jdQuestionKeys = {
  questions: (id: number) => ["jds", "questions", id] as const,
  preview: (id: number) => ["jds", "preview", id] as const,
  policies: ["jd-policies"] as const,
};

/** Polls while any of the JD's own questions is still on its way through the pipeline. */
export function useJDQuestions(id: number) {
  return useQuery({
    queryKey: jdQuestionKeys.questions(id),
    queryFn: async () => unwrap(await api.GET("/jds/{jd_id}/questions", { params: { path: { jd_id: id } } })),
    refetchInterval: (query) => ((query.state.data?.counts.own_waiting ?? 0) > 0 ? 5_000 : false),
  });
}

function useInvalidate(id: number) {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: jdKeys.all });
    void client.invalidateQueries({ queryKey: jdQuestionKeys.questions(id) });
    void client.invalidateQueries({ queryKey: ["questions"] });
    void client.invalidateQueries({ queryKey: ["jobs"] });
  };
}

/** Writes that answer with the whole Questions tab swap it in rather than refetching. */
function useTabWrite<T>(id: number, write: (input: T) => Promise<JDQuestions>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: write,
    onSuccess: (data) => {
      client.setQueryData(jdQuestionKeys.questions(id), data);
      void client.invalidateQueries({ queryKey: jdKeys.all });
      void client.invalidateQueries({ queryKey: ["questions"] });
    },
  });
}

export interface AddQuestionsInput {
  text: string;
  one: boolean;
  stack: string | null;
  scope: Scope | null;
}

export function useAddJDQuestions(id: number) {
  const invalidate = useInvalidate(id);
  return useMutation({
    mutationFn: async (body: AddQuestionsInput) =>
      unwrap(await api.POST("/jds/{jd_id}/questions", { params: { path: { jd_id: id } }, body })),
    onSuccess: invalidate,
  });
}

export function useAddJDQuestionFile(id: number) {
  const invalidate = useInvalidate(id);
  return useMutation({
    mutationFn: async (input: { file: File; stack: string | null; scope: Scope | null }) => {
      const { data, error } = await api.POST("/jds/{jd_id}/questions/file", {
        params: { path: { jd_id: id } },
        body: { file: input.file as unknown as string, stack: input.stack, scope: input.scope },
        bodySerializer: () => {
          const form = new FormData();
          form.append("file", input.file);
          if (input.stack) form.append("stack", input.stack);
          if (input.scope) form.append("scope", input.scope);
          return form;
        },
      });
      if (error) throw new Error(problemMessage(error));
      if (!data) throw new Error("The API returned no body");
      return data;
    },
    onSuccess: invalidate,
  });
}

export function useEditJDLink(id: number) {
  return useTabWrite(
    id,
    async ({ questionId, ...body }: { questionId: number; is_pinned?: boolean; is_excluded?: boolean }) =>
      unwrap(
        await api.PATCH("/jds/{jd_id}/questions/{question_id}", {
          params: { path: { jd_id: id, question_id: questionId } },
          body,
        }),
      ),
  );
}

export function useRemoveJDLink(id: number) {
  return useTabWrite(id, async (questionId: number) =>
    unwrap(
      await api.DELETE("/jds/{jd_id}/questions/{question_id}", {
        params: { path: { jd_id: id, question_id: questionId } },
      }),
    ),
  );
}

/** A Resource Manager's own approval, to this JD only — when `jd_rm_self_approve` allows it. */
export function useApproveForJD(id: number) {
  return useTabWrite(id, async (questionId: number) =>
    unwrap(
      await api.POST("/jds/{jd_id}/questions/{question_id}/approve", {
        params: { path: { jd_id: id, question_id: questionId } },
        body: { note: null },
      }),
    ),
  );
}

export function usePromoteJDQuestions(id: number) {
  return useTabWrite(id, async (questionIds: number[]) =>
    unwrap(
      await api.POST("/jds/{jd_id}/questions/promote", {
        params: { path: { jd_id: id } },
        body: { question_ids: questionIds },
      }),
    ),
  );
}

export function useRematchJD(id: number) {
  const invalidate = useInvalidate(id);
  return useMutation({
    mutationFn: async () =>
      unwrap(await api.POST("/jds/{jd_id}/rematch", { params: { path: { jd_id: id } } })),
    onSuccess: invalidate,
  });
}

/** A pick for this JD, as ipp-platform would get it. A mutation, not a query: each one is a fresh draw. */
export function useJDPreview(id: number) {
  return useMutation({
    mutationFn: async (body: { buckets: Record<string, number>; experience_years: number | null }) =>
      unwrap(await api.POST("/jds/{jd_id}/preview", { params: { path: { jd_id: id } }, body })),
  });
}

export function useJDPolicies() {
  return useQuery({
    queryKey: jdQuestionKeys.policies,
    queryFn: async () => unwrap(await api.GET("/jd-policies", {})),
  });
}

function usePolicyWrite<T>(write: (input: T) => Promise<JDPolicy[]>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: write,
    onSuccess: (data) => {
      client.setQueryData(jdQuestionKeys.policies, data);
      void client.invalidateQueries({ queryKey: jdKeys.all });
    },
  });
}

export function useCreatePolicy() {
  return usePolicyWrite(async (body: { name: string; shares: Record<string, number>; note: string | null }) =>
    unwrap(await api.POST("/jd-policies", { body })),
  );
}

export function useEditPolicy() {
  return usePolicyWrite(
    async ({
      id,
      ...body
    }: {
      id: number;
      name?: string;
      shares?: Record<string, number>;
      note?: string | null;
      is_default?: boolean;
    }) => unwrap(await api.PATCH("/jd-policies/{policy_id}", { params: { path: { policy_id: id } }, body })),
  );
}

export function useDeletePolicy() {
  return usePolicyWrite(async (id: number) =>
    unwrap(await api.DELETE("/jd-policies/{policy_id}", { params: { path: { policy_id: id } } })),
  );
}
